import React, { useState, useEffect, useRef } from "react";
import Chart from "react-apexcharts";
import { withErrorSuppression } from "utils/errorSuppressionUtils";
import ChartErrorBoundary from "./ChartErrorBoundary";
import { Spinner } from "components";

const GrowthChartInternal = ({ series, labels }) => {
  const [retryKey, setRetryKey] = useState(0);
  const [fadeIn, setFadeIn] = useState(false);
  const [chartReady, setChartReady] = useState(false);
  const initialRenderRef = useRef(true);
  const chartRef = useRef(null);

  // Process data before rendering
  const processData = () => {
    // Default values for empty data
    if (!Array.isArray(series) || series.length === 0) {
      return {
        processedSeries: [
          {
            name: "Yearly Signups",
            data: [0, 0, 0, 0, 0, 0, 0, 0],
          },
        ],
        processedLabels: Array(8)
          .fill(0)
          .map((_, i) => (new Date().getFullYear() - 7 + i).toString()),
      };
    }

    // Handle series data coming as a plain array
    if (!Array.isArray(series[0])) {
      // Convert to ApexCharts series format
      return {
        processedSeries: [
          {
            name: "Yearly Signups",
            data: series,
          },
        ],
        processedLabels:
          Array.isArray(labels) && labels.length > 0
            ? labels
            : Array(series.length)
                .fill(0)
                .map((_, i) =>
                  (
                    new Date().getFullYear() -
                    (series.length - 1) +
                    i
                  ).toString()
                ),
      };
    }

    // If series is already in ApexCharts format, use it directly
    return {
      processedSeries: series,
      processedLabels:
        labels ||
        Array(series[0].data.length)
          .fill(0)
          .map((_, i) =>
            (
              new Date().getFullYear() -
              (series[0].data.length - 1) +
              i
            ).toString()
          ),
    };
  };

  // Delayed initialization to prevent the ApexCharts error
  useEffect(() => {
    if (initialRenderRef.current) {
      // For first render, use a longer delay to ensure DOM is fully ready
      const timer = setTimeout(() => {
        setChartReady(true);
        initialRenderRef.current = false;

        // Add another delay for fade-in
        setTimeout(() => {
          setFadeIn(true);
        }, 100);
      }, 500); // Even longer delay for first render

      return () => clearTimeout(timer);
    } else {
      // For subsequent renders, use shorter delay
      setFadeIn(false);
      setChartReady(false);

      const timer = setTimeout(() => {
        setChartReady(true);

        // Add another delay for fade-in
        setTimeout(() => {
          setFadeIn(true);
        }, 50);
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [series, labels]);

  // Handle DOM errors with window.onerror
  useEffect(() => {
    const originalOnError = window.onerror;

    window.onerror = function (message, source, lineno, colno, error) {
      if (
        (source && source.includes("apexcharts")) ||
        (message && message.includes("apexcharts")) ||
        (error && error.stack && error.stack.includes("apexcharts"))
      ) {
        // Suppress ApexCharts errors
        return true; // Prevents the error from propagating
      }

      // Call original handler for other errors
      if (originalOnError) {
        return originalOnError(message, source, lineno, colno, error);
      }

      return false;
    };

    return () => {
      window.onerror = originalOnError;
    };
  }, []);

  // Handle retry when error boundary catches an error
  const handleRetry = () => {
    setRetryKey((prev) => prev + 1);
    setFadeIn(false);
    setChartReady(false);

    setTimeout(() => {
      setChartReady(true);
      setTimeout(() => {
        setFadeIn(true);
      }, 50);
    }, 100);
  };

  // If chart is not ready, show loading
  if (!chartReady) {
    return (
      <div className="w-full h-full flex-1 flex items-center justify-center py-6">
        <Spinner size="large" color="#19a8a6" />
      </div>
    );
  }

  const { processedSeries, processedLabels } = processData();

  // Create safe options with additional error checking
  const options = {
    chart: {
      type: "area",
      toolbar: { show: false },
      fontFamily: "inherit",
      background: "transparent",
      sparkline: { enabled: false },
      animations: { enabled: false },
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      curve: "straight",
      dashArray: 5,
      width: 2,
    },
    xaxis: {
      axisBorder: {
        show: true,
        borderColor: "#737373",
      },
      axisTicks: {
        show: true,
        borderColor: "#737373",
      },
      categories: processedLabels,
      labels: {
        style: {
          colors: "#737373",
          fontSize: "12px",
        },
      },
    },
    yaxis: {
      labels: {
        style: {
          colors: "#737373",
          fontSize: "12px",
        },
        formatter: (value) => Math.round(value),
      },
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: true,
        borderColor: "#737373",
      },
    },
    grid: {
      show: true,
      xaxis: {
        lines: {
          show: true,
          borderColor: "#737373",
        },
      },
      yaxis: {
        lines: {
          show: true,
          borderColor: "#737373",
        },
      },
    },
    colors: ["#FFB74D"],
    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 0.7,
        opacityFrom: 0.7,
        opacityTo: 0.4,
        stops: [0, 90, 100],
      },
    },
    tooltip: {
      enabled: true,
      theme: "light",
      x: {
        show: true,
        formatter: (val) => `Year: ${val}`,
      },
      y: {
        formatter: (val) => `Total Signups: ${val}`,
      },
    },
  };

  return (
    <ChartErrorBoundary resetKey={retryKey} onRetry={handleRetry}>
      <div
        className={`w-full h-full flex-1 transition-opacity duration-300 ease-in-out ${
          fadeIn ? "opacity-100" : "opacity-0"
        }`}
      >
        {chartReady && (
          <Chart
            options={options}
            series={processedSeries}
            type="area"
            width="100%"
            height="100%"
            ref={chartRef}
            key={`growth-chart-${retryKey}-${Math.random()}`}
          />
        )}
      </div>
    </ChartErrorBoundary>
  );
};

// Wrap the component with error suppression HOC
const GrowthChart = withErrorSuppression(GrowthChartInternal);

export default React.memo(GrowthChart);
